import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import type { V1SecretSchema } from '@/generated/hooks/gringotts/models';
import {
  getListSecretsQueryKey,
  useAddSecretVersion,
  useAddSecretVersion2,
  useCreateSecret,
  useCreateSecret2,
  useDeleteSecret,
  useDeleteSecret2,
} from '@/generated/hooks/gringotts/v1/services/secret_service';
import { captureException } from '@/modules/sentry';

import { ECredentialScope } from '../types';
import { toPayloadFields } from '../utils';

/** Arguments for {@link useCreateCredential}'s `createCredential`. */
export interface ICreateCredentialArgs {
  scope: ECredentialScope;
  displayName: string;
  /** Secret type name (the trailing segment of `secretTypes/{name}`). */
  secretTypeName: string;
  /** The type's schema, used to encode dictionary fields. */
  schema?: V1SecretSchema;
  /** Field id → value. */
  values: Record<string, string>;
}

/**
 * Creates a credential with its first version. Gringotts models this as two
 * calls (metadata, then a version); if the version fails the half-created
 * credential is deleted so it never shows up empty in a picker.
 *
 * @param organizationId - Current organization id.
 * @param workspaceId - Current workspace id.
 * @returns `createCredential` (resolves to the new credential's id) and its pending state.
 */
export function useCreateCredential(
  organizationId: string,
  workspaceId: string,
) {
  const queryClient = useQueryClient();
  const createOrgSecret = useCreateSecret();
  const createWorkspaceSecret = useCreateSecret2();
  const addOrgVersion = useAddSecretVersion();
  const addWorkspaceVersion = useAddSecretVersion2();
  const deleteOrgSecret = useDeleteSecret();
  const deleteWorkspaceSecret = useDeleteSecret2();

  const createCredential = useCallback(
    async ({
      scope,
      displayName,
      secretTypeName,
      schema,
      values,
    }: ICreateCredentialArgs): Promise<string> => {
      const isWorkspace = scope === ECredentialScope.Workspace;
      const data = {
        secret: { display_name: displayName, secret_type: secretTypeName },
      };
      const created = isWorkspace
        ? await createWorkspaceSecret.mutateAsync({
            organizationId,
            workspaceId,
            data,
          })
        : await createOrgSecret.mutateAsync({ organizationId, data });
      const credentialId = created.data.id ?? '';
      const version = { payload: { fields: toPayloadFields(values, schema) } };

      try {
        if (isWorkspace) {
          await addWorkspaceVersion.mutateAsync({
            organizationId,
            workspaceId,
            id: credentialId,
            data: version,
          });
        } else {
          await addOrgVersion.mutateAsync({
            organizationId,
            id: credentialId,
            data: version,
          });
        }
      } catch (error) {
        const cleanup = isWorkspace
          ? deleteWorkspaceSecret.mutateAsync({
              organizationId,
              workspaceId,
              id: credentialId,
            })
          : deleteOrgSecret.mutateAsync({ organizationId, id: credentialId });
        await cleanup.catch(cleanupError =>
          captureException(cleanupError, {
            tags: { module: 'credentials', action: 'createCredentialCleanup' },
          }),
        );
        throw error;
      } finally {
        void queryClient.invalidateQueries({
          queryKey: getListSecretsQueryKey(organizationId),
        });
      }

      return credentialId;
    },
    [
      organizationId,
      workspaceId,
      queryClient,
      createOrgSecret,
      createWorkspaceSecret,
      addOrgVersion,
      addWorkspaceVersion,
      deleteOrgSecret,
      deleteWorkspaceSecret,
    ],
  );

  return {
    createCredential,
    isPending:
      createOrgSecret.isPending ||
      createWorkspaceSecret.isPending ||
      addOrgVersion.isPending ||
      addWorkspaceVersion.isPending,
  };
}
